import { InfoIcon, ShieldAlertIcon, ShieldCheckIcon, ShieldIcon, ShieldXIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { FieldHeader } from '@/components/atoms/FieldHeader/FieldHeader';
import { FieldsetCard } from '@/components/atoms/FieldsetCard';
import { SectionLayout } from '@/components/atoms/SectionLayout/SectionLayout';
import DurationInput from '@/components/organisms/DurationInput/DurationInput';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { useAccount } from '@/hooks/useAccount';
import useBrains from '@/hooks/useBrains';
import { useTrackFormState } from '@/hooks/useTrackFormState';
import { getPermissions } from '@/redux/permissions/selectors';
import { Brain, FactualitySensitivity } from '@/shared/types/brain';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { CLEAR_DIRTY_VALUES, KEEP_DIRTY_VALUES } from '@/util/forms/resetOptions';

const FORM_ID = 'advanced-settings-form';

interface AdvancedSettingsForm {
  confidence_threshold: number;
  inactivity_timeout: number;
  factuality_sensitivity: FactualitySensitivity | undefined;
}

const FACTUALITY_OPTIONS = [
  { value: FactualitySensitivity.NONE, icon: ShieldXIcon },
  { value: FactualitySensitivity.LOW, icon: ShieldIcon },
  { value: FactualitySensitivity.MEDIUM, icon: ShieldCheckIcon },
  { value: FactualitySensitivity.HIGH, icon: ShieldAlertIcon },
] as const;

const FACTUALITY_LABEL_KEYS: Record<FactualitySensitivity, string> = {
  [FactualitySensitivity.NONE]:
    'ai_agents.advanced_settings.factuality_sensitivity.options.none.label',
  [FactualitySensitivity.LOW]:
    'ai_agents.advanced_settings.factuality_sensitivity.options.low.label',
  [FactualitySensitivity.MEDIUM]:
    'ai_agents.advanced_settings.factuality_sensitivity.options.medium.label',
  [FactualitySensitivity.HIGH]:
    'ai_agents.advanced_settings.factuality_sensitivity.options.high.label',
};

const FACTUALITY_DESCRIPTION_KEYS: Record<FactualitySensitivity, string> = {
  [FactualitySensitivity.NONE]:
    'ai_agents.advanced_settings.factuality_sensitivity.options.none.description',
  [FactualitySensitivity.LOW]:
    'ai_agents.advanced_settings.factuality_sensitivity.options.low.description',
  [FactualitySensitivity.MEDIUM]:
    'ai_agents.advanced_settings.factuality_sensitivity.options.medium.description',
  [FactualitySensitivity.HIGH]:
    'ai_agents.advanced_settings.factuality_sensitivity.options.high.description',
};

interface AdvancedSettingsFieldsetProps {
  brain: Brain;
}

export const AdvancedSettingsFieldset = ({ brain }: AdvancedSettingsFieldsetProps) => {
  const { t } = useTranslation();
  const { account } = useAccount();
  const { updateBrainAsync } = useBrains(brain.brain_id);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );
  const maxInactivityTimeout = account?.max_inactivity_timeout;

  const form = useForm<AdvancedSettingsForm>({
    mode: 'onTouched',
    resetOptions: KEEP_DIRTY_VALUES,
    values: {
      confidence_threshold: brain.confidence_threshold ?? 0.0,
      inactivity_timeout: brain.inactivity_timeout ?? 0,
      factuality_sensitivity: brain.guidelines?.factuality_sensitivity || undefined,
    },
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty, isSubmitting },
  } = form;
  useTrackFormState({ isDirty, formId: FORM_ID });

  const onSubmit = async (data: AdvancedSettingsForm) => {
    await updateBrainAsync({
      brain_id: brain.brain_id,
      confidence_threshold: data.confidence_threshold,
      inactivity_timeout: data.inactivity_timeout,
      guidelines: {
        ...brain.guidelines,
        ...(data.factuality_sensitivity !== undefined && {
          factuality_sensitivity: data.factuality_sensitivity,
        }),
      },
    });
    form.reset(data, CLEAR_DIRTY_VALUES);
  };

  return (
    <div id="advanced-settings">
      <SectionLayout title={t('ai_agents.advanced_settings.title')}>
        <Form {...form}>
          <form onSubmit={handleSubmit(onSubmit)} id={FORM_ID}>
            <FieldsetCard
              isDirty={isDirty}
              isLoading={isSubmitting}
              disabled={!isDirty || !canWrite}
              saveButtonLabel={t('common.save')}
              formId={FORM_ID}
            >
              <div className="flex flex-col gap-6">
                {/* Inactivity Timeout */}
                <FormField
                  control={control}
                  name="inactivity_timeout"
                  render={({ field }) => (
                    <FormItem>
                      <FieldHeader title={t('brains.inactivity.title')} />
                      <DurationInput
                        {...field}
                        className="mt-0"
                        disabled={!canWrite}
                        minSeconds={300}
                        maxSeconds={maxInactivityTimeout ?? 14 * 86400}
                      />
                      <FormDescription>{t('brains.inactivity.subtitle')}</FormDescription>
                    </FormItem>
                  )}
                />

                {/* Factuality Sensitivity */}
                <FormField
                  control={control}
                  name="factuality_sensitivity"
                  render={({ field }) => (
                    <FormItem>
                      <FieldHeader
                        title={t('ai_agents.advanced_settings.factuality_sensitivity.title')}
                        tooltip={t('ai_agents.advanced_settings.factuality_sensitivity.tooltip')}
                      />
                      <Select
                        disabled={!canWrite}
                        onValueChange={field.onChange}
                        value={field.value || undefined}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={t('common.select_an_option')}>
                              {field.value && t(FACTUALITY_LABEL_KEYS[field.value])}
                            </SelectValue>
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="max-w-md">
                          {FACTUALITY_OPTIONS.map((option) => {
                            const Icon = option.icon;
                            return (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                                className="max-w-md py-3 pl-3 data-[state=checked]:bg-muted [&>span:first-child]:hidden"
                              >
                                <div className="flex items-start gap-4">
                                  <Icon className="mt-0.5 size-5 shrink-0 text-muted-foreground" />
                                  <div className="flex flex-col gap-1">
                                    <div className="flex items-center gap-1.5 leading-none font-medium">
                                      {t(FACTUALITY_LABEL_KEYS[option.value])}
                                      {option.value === FactualitySensitivity.LOW && (
                                        <span className="text-muted-foreground">
                                          {t(
                                            'ai_agents.advanced_settings.factuality_sensitivity.options.low.recommended'
                                          )}
                                        </span>
                                      )}
                                    </div>
                                    <span className="text-sm leading-5 text-muted-foreground">
                                      {t(FACTUALITY_DESCRIPTION_KEYS[option.value])}
                                    </span>
                                  </div>
                                </div>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        {t('ai_agents.advanced_settings.factuality_sensitivity.subtitle')}
                      </FormDescription>
                      <FormMessage />
                      {field.value === FactualitySensitivity.HIGH && (
                        <Alert className="mt-4 border-border bg-info-soft [&>svg]:text-foreground">
                          <InfoIcon />
                          <AlertTitle>
                            {t(
                              'ai_agents.advanced_settings.factuality_sensitivity.maximum_guard_alert.title'
                            )}
                          </AlertTitle>
                          <AlertDescription>
                            {t(
                              'ai_agents.advanced_settings.factuality_sensitivity.maximum_guard_alert.description'
                            )}
                          </AlertDescription>
                        </Alert>
                      )}
                    </FormItem>
                  )}
                />

                {/* Confidence Threshold */}
                <FormField
                  control={control}
                  name="confidence_threshold"
                  render={({ field }) => (
                    <FormItem>
                      <FieldHeader
                        title={t('ai_agents.advanced_settings.prediction_threshold.title')}
                      />
                      <FormControl>
                        <Slider
                          tooltip
                          displayRealValue
                          min={0}
                          max={0.99}
                          step={0.05}
                          minLabel={t('ai_agents.confidence_threshold.flexible')}
                          maxLabel={t('ai_agents.confidence_threshold.conservative')}
                          disabled={!canWrite}
                          value={[field.value]}
                          onValueChange={([value]) => field.onChange(value)}
                        />
                      </FormControl>
                      <FormDescription>
                        {t('ai_agents.advanced_settings.prediction_threshold.subtitle')}
                      </FormDescription>
                    </FormItem>
                  )}
                />
              </div>
            </FieldsetCard>
          </form>
        </Form>
      </SectionLayout>
    </div>
  );
};
